import type { EntityPermission, User, UserGroup } from '@gmn-portal/backend';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  type EntityPermissionOverviewEntry,
  useCreateEntityPermission,
  useDeleteEntityPermission,
  useEntityPermissionOverview,
  useEntityPermissions,
  useUpdateEntityPermission,
  useUserGroups,
  useUsers,
  type Where,
} from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { PaginationNavigation } from '@/shared/components/pagination';
import { TableSearchBar } from '@/shared/components/tables';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Label,
} from '@/shared/components/ui';
import { useDebounce } from '@/shared/hooks/use-debounce';
import {
  type AclServiceKey,
  canReadService,
  getServiceScopes,
  hasServicePermission,
  isCustomerAdminGroup,
  isGmnAdminGroup,
  type ScopeOption,
  serviceRegistry,
} from '@/shared/utils/permissions';
import { UsersAccessTable } from './users-access-table';

type EntityPermissionsTabProps = {
  entityId: string;
  entityType: AclServiceKey;
  customerId?: string | null;
};

const resolveCustomerId = (value?: string | { id: string } | null) => {
  if (!value) return undefined;
  return typeof value === 'string' ? value : value.id;
};

const getUserId = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && 'id' in value && typeof (value as any).id === 'string') {
    return (value as any).id;
  }
  return null;
};

const getUserLabel = (user: User) => {
  const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return fullName !== '' ? fullName : user.email || user.id;
};

export function EntityPermissionsTab({
  entityId,
  entityType,
  customerId,
}: EntityPermissionsTabProps) {
  const { t } = useIntl();
  const { user: currentUser } = useAuth();

  const isGmnAdmin = isGmnAdminGroup(currentUser);
  const isCustomerAdmin = isCustomerAdminGroup(currentUser);
  const isAuthorized = isGmnAdmin || isCustomerAdmin;
  const isCustomerView = isCustomerAdmin && !isGmnAdmin;

  const resolvedCustomerId =
    customerId ??
    resolveCustomerId(currentUser?.customer as string | { id: string } | null | undefined);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [gmnPage, setGmnPage] = useState(1);
  const [gmnPageSize, setGmnPageSize] = useState(10);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerPageSize, setCustomerPageSize] = useState(10);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { data: userGroupsData } = useUserGroups({
    limit: 0,
    sort: 'key',
    disabled: !isAuthorized,
  });
  const userGroups = userGroupsData?.docs || [];
  const userGroupsById = useMemo(
    () => new Map(userGroups.map((group) => [group.id, group])),
    [userGroups]
  );
  const gmnGroupIds = userGroups.filter((group) => group.domain === 'gmn').map((group) => group.id);
  const customerGroupIds = userGroups
    .filter((group) => group.domain === 'customer')
    .map((group) => group.id);

  const searchFilter = useMemo<Where | null>(() => {
    const value = debouncedSearch.trim();
    if (!value) return null;
    const conditions: Where[] = [
      { email: { like: value } },
      { firstName: { like: value } },
      { lastName: { like: value } },
    ];
    return {
      or: conditions,
    };
  }, [debouncedSearch]);

  const gmnWhere = useMemo(() => {
    if (!isGmnAdmin || gmnGroupIds.length === 0) return undefined;
    const and: Where[] = [{ userGroup: { in: gmnGroupIds } }, { customer: { exists: false } }];
    if (searchFilter) {
      and.push(searchFilter);
    }
    return { and };
  }, [gmnGroupIds, isGmnAdmin, searchFilter]);

  const customerWhere = useMemo(() => {
    if (customerGroupIds.length === 0 || !resolvedCustomerId) return undefined;
    const and: Where[] = [
      { userGroup: { in: customerGroupIds } },
      { customer: { equals: resolvedCustomerId } },
    ];
    if (searchFilter) {
      and.push(searchFilter);
    }
    return { and };
  }, [customerGroupIds, resolvedCustomerId, searchFilter]);

  const { data: gmnUsersData } = useUsers({
    depth: 0,
    page: gmnPage,
    limit: gmnPageSize,
    sort: 'lastName',
    where: gmnWhere,
    disabled: !isAuthorized || !isGmnAdmin || !gmnWhere,
  });

  const { data: customerUsersData } = useUsers({
    depth: 0,
    page: customerPage,
    limit: customerPageSize,
    sort: 'lastName',
    where: customerWhere,
    disabled: !isAuthorized || !customerWhere,
  });

  useEffect(() => {
    setGmnPage(1);
    setCustomerPage(1);
  }, [debouncedSearch, resolvedCustomerId]);

  useEffect(() => {
    if (gmnUsersData && gmnPage > gmnUsersData.totalPages) {
      setGmnPage(Math.max(1, gmnUsersData.totalPages));
    }
  }, [gmnPage, gmnUsersData]);

  useEffect(() => {
    if (customerUsersData && customerPage > customerUsersData.totalPages) {
      setCustomerPage(Math.max(1, customerUsersData.totalPages));
    }
  }, [customerPage, customerUsersData]);

  const users = useMemo(() => {
    const gmnUsers = isGmnAdmin ? gmnUsersData?.docs || [] : [];
    const customerUsers = isGmnAdmin || isCustomerAdmin ? customerUsersData?.docs || [] : [];
    return {
      gmn: gmnUsers as User[],
      customer: customerUsers as User[],
    };
  }, [customerUsersData?.docs, gmnUsersData?.docs, isCustomerAdmin, isGmnAdmin]);

  const overviewUserIds = useMemo(() => {
    const ids = [...users.gmn, ...users.customer].map((user) => user.id);
    return Array.from(new Set(ids));
  }, [users.customer, users.gmn]);

  const {
    data: overviewData,
    isLoading: isOverviewLoading,
    isFetching: isFetchingOverview,
  } = useEntityPermissionOverview({
    entityType,
    entityId,
    userIds: overviewUserIds,
    disabled: !isAuthorized || overviewUserIds.length === 0,
  });

  const hasOverview = Boolean(overviewData);

  const overviewByUserId = useMemo(() => {
    const map = new Map<string, EntityPermissionOverviewEntry>();
    for (const entry of overviewData?.entries ?? []) {
      map.set(entry.userId, entry);
    }
    return map;
  }, [overviewData?.entries]);

  const { data: permissionsData } = useEntityPermissions({
    limit: 0,
    depth: 0,
    where:
      overviewUserIds.length > 0
        ? {
            and: [
              { entityType: { equals: entityType } },
              { entityId: { equals: entityId } },
              { user: { in: overviewUserIds } },
            ],
          }
        : undefined,
    disabled: !isAuthorized || overviewUserIds.length === 0,
  });

  const permissionEntries = (permissionsData?.docs || []) as EntityPermission[];
  const permissionByUserId = useMemo(() => {
    const map = new Map<string, EntityPermission>();
    permissionEntries.forEach((entry) => {
      const userId = getUserId(entry.user);
      if (userId) map.set(userId, entry);
    });
    return map;
  }, [permissionEntries]);

  const createPermission = useCreateEntityPermission();
  const updatePermission = useUpdateEntityPermission();
  const deletePermission = useDeleteEntityPermission();
  const isSaving =
    createPermission.isPending || updatePermission.isPending || deletePermission.isPending;

  const resolveGroupLabel = (group: UserGroup | null) => {
    const labelKey = `portalRoles.${group?.key ?? ''}`;
    const translated = group?.key ? t(labelKey as unknown as never) : '';
    return group?.key && translated !== labelKey ? translated : (group?.key ?? '-');
  };

  const resolveGroup = (user: User): UserGroup | null => {
    const group = user.userGroup;
    if (!group) return null;
    if (typeof group === 'string') {
      return userGroupsById.get(group) ?? null;
    }
    return group as UserGroup;
  };

  const selectedUser = useMemo(() => {
    if (!selectedUserId) return null;
    return [...users.gmn, ...users.customer].find((user) => user.id === selectedUserId) ?? null;
  }, [selectedUserId, users.customer, users.gmn]);

  const selectedReadId = selectedUser
    ? `entity-permissions-read-${selectedUser.id}`
    : 'entity-permissions-read';
  const selectedUpdateId = selectedUser
    ? `entity-permissions-update-${selectedUser.id}`
    : 'entity-permissions-update';

  const selectedGroup = selectedUser ? resolveGroup(selectedUser) : null;
  const selectedGroupLabel = selectedGroup ? resolveGroupLabel(selectedGroup) : '-';
  const selectedGroupLink = selectedGroup?.key
    ? `/gmn/settings/user-groups?group=${encodeURIComponent(selectedGroup.key)}`
    : null;
  const selectedPermission = selectedUser ? permissionByUserId.get(selectedUser.id) : null;
  const selectedOverview = selectedUser ? overviewByUserId.get(selectedUser.id) : null;
  const selectedReadAccess = selectedOverview?.read ?? { allowed: false, sources: [] };
  const selectedUpdateAccess = selectedOverview?.update ?? { allowed: false, sources: [] };
  const selectedScopes = selectedGroup ? getServiceScopes(selectedGroup, entityType) : [];
  const selectedHasReadPermission = selectedGroup
    ? hasServicePermission(selectedGroup, entityType, 'read')
    : false;
  const selectedHasUpdatePermission = selectedGroup
    ? hasServicePermission(selectedGroup, entityType, 'update')
    : false;
  const selectedSupportsExplicitApproval = selectedScopes.includes('explicit-approval');
  const selectedCanRead =
    selectedSupportsExplicitApproval && canReadService(selectedGroup, entityType);
  const selectedCanUpdate =
    selectedSupportsExplicitApproval &&
    selectedHasUpdatePermission &&
    canReadService(selectedGroup, entityType);
  const selectedReadChecked = Boolean(selectedPermission?.read);
  const selectedUpdateChecked = Boolean(selectedPermission?.update);
  const selectedExplicitReadMessage = !selectedSupportsExplicitApproval
    ? t('permissions.explicitNotApplicable')
    : !selectedHasReadPermission
      ? t('permissions.missingReadPermission')
      : null;
  const selectedExplicitUpdateMessage = !selectedSupportsExplicitApproval
    ? t('permissions.explicitNotApplicable')
    : !selectedHasReadPermission
      ? t('permissions.missingReadPermission')
      : !selectedHasUpdatePermission
        ? t('permissions.missingUpdatePermission')
        : null;
  const renderSourceBadges = (sources: ScopeOption[]) => {
    if (sources.length === 0) {
      return (
        <span className="text-xs text-muted-foreground">{t('permissions.noAccessSource')}</span>
      );
    }
    return (
      <div className="flex flex-wrap gap-2">
        {sources.map((source) => (
          <Badge key={source} variant="outline-solid">
            {t(`permissions.scopes.${source}` as unknown as never)}
          </Badge>
        ))}
      </div>
    );
  };

  const renderScopeBadges = (scopes: ScopeOption[]) => {
    if (scopes.length === 0) {
      return (
        <span className="text-xs text-muted-foreground">{t('permissions.noGroupScopes')}</span>
      );
    }
    return (
      <div className="flex flex-wrap gap-2">
        {scopes.map((scope) => (
          <Badge key={scope} variant="outline-solid">
            {t(`permissions.scopes.${scope}` as unknown as never)}
          </Badge>
        ))}
      </div>
    );
  };

  const renderAccessBadge = (allowed: boolean | null) => {
    return (
      <Badge variant={allowed === null ? 'secondary' : allowed ? 'outline-solid' : 'secondary'}>
        {allowed === null
          ? t('permissions.accessLoading')
          : allowed
            ? t('permissions.accessGranted')
            : t('permissions.accessDenied')}
      </Badge>
    );
  };

  const serviceLabel = t(serviceRegistry[entityType].labelKey as unknown as never);

  const resolveSourceReason = (source: ScopeOption) => {
    switch (source) {
      case 'all':
        return t('permissions.reasons.sources.all', { service: serviceLabel } as never);
      case 'customer-assignment':
        return t('permissions.reasons.sources.customerAssignment', {
          service: serviceLabel,
        } as never);
      case 'user-assignment':
        return selectedGroup?.domain === 'gmn'
          ? t('permissions.reasons.sources.userAssignmentGmn')
          : t('permissions.reasons.sources.userAssignmentCustomer');
      case 'explicit-approval':
        return t('permissions.reasons.sources.explicitApproval');
      default:
        return '';
    }
  };

  const buildReasonList = (action: 'read' | 'update') => {
    if (!selectedOverview) {
      return { status: 'loading' as const, reasons: [t('permissions.reasons.loading')] };
    }

    const access = action === 'read' ? selectedReadAccess : selectedUpdateAccess;
    if (access.allowed) {
      const reasons = access.sources
        .map(resolveSourceReason)
        .filter((reason) => reason.trim().length > 0);
      return { status: 'allowed' as const, reasons };
    }

    if (action === 'update') {
      if (!selectedHasUpdatePermission) {
        return {
          status: 'denied' as const,
          reasons: [t('permissions.reasons.denied.updatePermission')],
        };
      }
      if (!selectedHasReadPermission) {
        return {
          status: 'denied' as const,
          reasons: [t('permissions.reasons.denied.readRequired')],
        };
      }
    }

    if (!selectedHasReadPermission) {
      return {
        status: 'denied' as const,
        reasons: [t('permissions.reasons.denied.readPermission')],
      };
    }

    if (selectedScopes.length === 0) {
      return {
        status: 'denied' as const,
        reasons: [t('permissions.reasons.denied.noScope')],
      };
    }

    return {
      status: 'denied' as const,
      reasons: [t('permissions.reasons.denied.noSource')],
    };
  };

  const handleToggle = async (user: User, next: { read: boolean; update: boolean }) => {
    const userId = user.id;
    const existing = permissionByUserId.get(userId);

    try {
      if (!next.read && !next.update) {
        if (existing) {
          await deletePermission.mutateAsync(existing.id);
        }
        return;
      }

      if (existing) {
        await updatePermission.mutateAsync({
          id: existing.id,
          data: { read: next.read, update: next.update },
        });
        return;
      }

      await createPermission.mutateAsync({
        entityType: entityType as EntityPermission['entityType'],
        entityId,
        user: userId,
        read: next.read,
        update: next.update,
      });
    } catch (error) {
      toast.error(t('permissions.updateFailed'));
      throw error;
    }
  };

  const handleSelectedReadChange = async (checked: boolean) => {
    if (!selectedUser) return;
    const next = { read: checked, update: checked ? selectedUpdateChecked : false };
    await handleToggle(selectedUser, next);
  };

  const handleSelectedUpdateChange = async (checked: boolean) => {
    if (!selectedUser) return;
    const next = { read: checked ? true : selectedReadChecked, update: checked };
    await handleToggle(selectedUser, next);
  };

  const gmnTotalPages = gmnUsersData?.totalPages ?? 0;
  const gmnTotalDocs = gmnUsersData?.totalDocs ?? 0;
  const customerTotalPages = customerUsersData?.totalPages ?? 0;
  const customerTotalDocs = customerUsersData?.totalDocs ?? 0;

  const renderTable = (rows: User[]) => {
    return (
      <div className="rounded-lg border border-border overflow-hidden">
        <UsersAccessTable
          users={rows}
          userGroupsById={userGroupsById}
          overviewByUserId={overviewByUserId}
          isOverviewLoading={isOverviewLoading || isFetchingOverview || !hasOverview}
          renderAction={({ user, group }) => {
            const entry = permissionByUserId.get(user.id);
            const readChecked = Boolean(entry?.read);
            const updateChecked = Boolean(entry?.update);
            const hasReadPermission = group
              ? hasServicePermission(group, entityType, 'read')
              : false;
            const scopes = group ? getServiceScopes(group, entityType) : [];
            const supportsExplicitApproval = scopes.includes('explicit-approval');
            const canRead = supportsExplicitApproval && canReadService(group, entityType);
            const canEditExplicit = supportsExplicitApproval && hasReadPermission && canRead;
            const actionLabel = canEditExplicit
              ? readChecked || updateChecked
                ? t('permissions.editApproval')
                : t('permissions.grantApproval')
              : t('permissions.viewAccess');

            return (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedUserId(user.id);
                  setDetailsOpen(true);
                }}
                aria-label={actionLabel}
              >
                {actionLabel}
              </Button>
            );
          }}
        />
      </div>
    );
  };

  if (!isAuthorized) {
    return <div className="text-sm text-muted-foreground">{t('permissions.notAuthorized')}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-card p-4 flex flex-col md:flex-row gap-4 items-center justify-between">
        <TableSearchBar
          searchTerm={search}
          onSearchChange={setSearch}
          placeholder={t('permissions.searchPlaceholder')}
        />
      </div>

      {isGmnAdmin && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">{t('permissions.gmnUsers')}</h3>
          {renderTable(users.gmn)}
          {gmnTotalPages > 1 && (
            <PaginationNavigation
              currentPage={gmnPage}
              totalPages={gmnTotalPages}
              pageSize={gmnPageSize}
              totalItems={gmnTotalDocs}
              onPageChange={setGmnPage}
              onPageSizeChange={setGmnPageSize}
              showPageSizeSelector
            />
          )}
        </div>
      )}

      <div className="space-y-3">
        <h3 className="text-sm font-semibold">{t('permissions.customerUsers')}</h3>
        {resolvedCustomerId ? (
          <>
            {renderTable(users.customer)}
            {customerTotalPages > 1 && (
              <PaginationNavigation
                currentPage={customerPage}
                totalPages={customerTotalPages}
                pageSize={customerPageSize}
                totalItems={customerTotalDocs}
                onPageChange={setCustomerPage}
                onPageSizeChange={setCustomerPageSize}
                showPageSizeSelector
              />
            )}
          </>
        ) : (
          <div className="text-sm text-muted-foreground">{t('permissions.assignCustomerHint')}</div>
        )}
      </div>

      <Dialog
        open={detailsOpen}
        onOpenChange={(open) => {
          setDetailsOpen(open);
          if (!open) {
            setSelectedUserId(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('permissions.dialogTitle')}</DialogTitle>
            <DialogDescription>
              {selectedUser
                ? `${getUserLabel(selectedUser)} · ${selectedGroupLabel}`
                : t('permissions.detailsPlaceholder')}
            </DialogDescription>
          </DialogHeader>

          {selectedUser ? (
            <div className="mt-4 space-y-4">
              <Card>
                <CardContent className="space-y-3">
                  <h4 className="text-sm font-semibold">{t('permissions.effectiveAccess')}</h4>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="flex items-center justify-between rounded-md border border-border p-3">
                      <span className="text-sm">{t('permissions.read')}</span>
                      {renderAccessBadge(selectedOverview ? selectedReadAccess.allowed : null)}
                    </div>
                    <div className="flex items-center justify-between rounded-md border border-border p-3">
                      <span className="text-sm">{t('permissions.update')}</span>
                      {renderAccessBadge(selectedOverview ? selectedUpdateAccess.allowed : null)}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {!isCustomerView && (
                <>
                  <Card>
                    <CardContent className="space-y-3">
                      <h4 className="text-sm font-semibold">{t('permissions.reasonsTitle')}</h4>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <div className="text-xs text-muted-foreground">
                            {t('permissions.read')}
                          </div>
                          <ul className="list-disc space-y-1 pl-4 text-sm text-muted-foreground">
                            {buildReasonList('read').reasons.map((reason) => (
                              <li key={reason}>{reason}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="space-y-2">
                          <div className="text-xs text-muted-foreground">
                            {t('permissions.update')}
                          </div>
                          <ul className="list-disc space-y-1 pl-4 text-sm text-muted-foreground">
                            {buildReasonList('update').reasons.map((reason) => (
                              <li key={reason}>{reason}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="space-y-4">
                      <h4 className="text-sm font-semibold">{t('permissions.accessLadder')}</h4>
                      <div className="space-y-4">
                        <div className="flex gap-3">
                          <div className="mt-0.5 flex size-6 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                            1
                          </div>
                          <div className="space-y-2">
                            <div className="text-sm font-medium">
                              {t('permissions.stepGroupPermissions')}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                              <span>{t('permissions.groupLabel')}</span>
                              <Badge variant="secondary">{selectedGroupLabel}</Badge>
                              {selectedGroupLink && (
                                <a
                                  href={selectedGroupLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="underline underline-offset-2"
                                >
                                  {t('permissions.viewGroup')}
                                </a>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2 text-xs">
                              <Badge
                                variant={selectedHasReadPermission ? 'outline-solid' : 'secondary'}
                              >
                                {t('permissions.read')}:{' '}
                                {selectedHasReadPermission ? t('common.yes') : t('common.no')}
                              </Badge>
                              <Badge
                                variant={
                                  selectedHasUpdatePermission ? 'outline-solid' : 'secondary'
                                }
                              >
                                {t('permissions.update')}:{' '}
                                {selectedHasUpdatePermission ? t('common.yes') : t('common.no')}
                              </Badge>
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-3">
                          <div className="mt-0.5 flex size-6 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                            2
                          </div>
                          <div className="space-y-2">
                            <div className="text-sm font-medium">{t('permissions.stepScopes')}</div>
                            {renderScopeBadges(selectedScopes)}
                          </div>
                        </div>

                        <div className="flex gap-3">
                          <div className="mt-0.5 flex size-6 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                            3
                          </div>
                          <div className="space-y-2">
                            <div className="text-sm font-medium">
                              {t('permissions.stepExplicitApproval')}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {selectedSupportsExplicitApproval
                                ? t('permissions.explicitApprovalHint')
                                : t('permissions.explicitNotApplicable')}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="space-y-4">
                      <h4 className="text-sm font-semibold">{t('permissions.accessSources')}</h4>
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <div className="text-xs text-muted-foreground">
                            {t('permissions.read')}
                          </div>
                          {selectedOverview ? (
                            renderSourceBadges(selectedReadAccess.sources)
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {t('permissions.accessLoading')}
                            </span>
                          )}
                        </div>
                        <div className="space-y-2">
                          <div className="text-xs text-muted-foreground">
                            {t('permissions.update')}
                          </div>
                          {selectedOverview ? (
                            renderSourceBadges(selectedUpdateAccess.sources)
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {t('permissions.accessLoading')}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}

              <Card>
                <CardContent className="space-y-4">
                  <h4 className="text-sm font-semibold">
                    {t('permissions.explicitApprovalTitle')}
                  </h4>
                  {selectedSupportsExplicitApproval ? (
                    <div className="space-y-3">
                      {selectedExplicitReadMessage ? (
                        <div className="text-xs text-muted-foreground">
                          {selectedExplicitReadMessage}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm">
                          <Checkbox
                            id={selectedReadId}
                            checked={selectedReadChecked}
                            onCheckedChange={(checked) =>
                              void handleSelectedReadChange(Boolean(checked))
                            }
                            disabled={!selectedCanRead || isSaving}
                          />
                          <Label htmlFor={selectedReadId}>{t('permissions.read')}</Label>
                        </div>
                      )}
                      {selectedExplicitUpdateMessage ? (
                        <div className="text-xs text-muted-foreground">
                          {selectedExplicitUpdateMessage}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm">
                          <Checkbox
                            id={selectedUpdateId}
                            checked={selectedUpdateChecked}
                            onCheckedChange={(checked) =>
                              void handleSelectedUpdateChange(Boolean(checked))
                            }
                            disabled={!selectedCanUpdate || isSaving}
                          />
                          <Label htmlFor={selectedUpdateId}>{t('permissions.update')}</Label>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">
                      {t('permissions.explicitNotApplicable')}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="mt-6 text-sm text-muted-foreground">
              {t('permissions.detailsEmpty')}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
